import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';

export type ToastVariant = 'info' | 'success' | 'error' | 'warning';

export interface Toast {
  id: string;
  message: string;
  /** Web-style alias: same as variant. */
  type?: ToastVariant;
  variant?: ToastVariant;
  duration?: number;
  /** Optional action displayed as a chevron+label button on the toast. */
  actionLabel?: string;
  onPress?: () => void;
}

interface ToastContextType {
  toasts: Toast[];
  /** RN-style entry point: showToast(message, opts). */
  showToast: (
    message: string,
    opts?: Partial<Omit<Toast, 'id' | 'message'>>,
  ) => void;
  /** Existing web entry point preserved for back-compat. */
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = Math.random().toString(36).slice(2, 11);
      const variant = toast.variant ?? toast.type ?? 'info';
      const newToast: Toast = {
        ...toast,
        id,
        variant,
        type: variant,
      };
      setToasts(prev => [...prev, newToast]);

      const duration = toast.duration ?? (toast.onPress ? 5000 : 3000);
      if (duration !== 0) {
        setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast],
  );

  const showToast = useCallback<ToastContextType['showToast']>(
    (message, opts) => {
      addToast({ message, ...opts });
    },
    [addToast],
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, showToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};
